import type { RecordSummary, StageMove } from "@shared/recordSummary";
import type { OriginationView } from "@shared/originationConstants";
import { formatDealValue } from "@shared/formatDealValue";
import { describeOrigination } from "@/components/origination/describeOrigination";
import type { Fact } from "../FactsStrip";
import { dayLabel, firstName } from "../recordFormat";
import { ftcContactFact, lastEmailFact, lastSpokeFact, openItemsFact, processesFact } from "./personFacts";

/** A stored amount (dollars, or typed like "$5M") for display; blank and zero read as unset. */
function money(v: string | number | null | undefined): string | null {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  if (parseFloat(String(v).replace(/[$,\s]/g, "")) === 0) return null;
  return formatDealValue(String(v)) || null;
}

/** "$10M to $50M", "$10M+", "up to $50M", or null: the Buyer Network list's wording. */
export function dealSizeLabel(min: string | number | null | undefined, max: string | number | null | undefined): string | null {
  const mn = money(min), mx = money(max);
  if (mn && mx) return mn === mx ? mn : `${mn} to ${mx}`;
  if (mn) return `${mn}+`;
  return mx ? `up to ${mx}` : null;
}

export function lastTouchFact(lastActivityAt: string | null | undefined, today: string): Fact {
  return { label: "Last touch", value: lastActivityAt ? dayLabel(lastActivityAt, today) : "None yet" };
}

export interface BuyerCriteriaFacts {
  minDealSize: string | null; maxDealSize: string | null; targetEbitdaMin: string | null;
  geoFocus: string | null; primaryGeographies: string[] | null;
}

export function buyerFacts(
  s: RecordSummary | undefined, today: string,
  b: { lastActivityAt: string | null; criteria: BuyerCriteriaFacts | null },
): Fact[] {
  const c = b.criteria;
  const size = c ? dealSizeLabel(c.minDealSize, c.maxDealSize) : null;
  const ebitda = c ? money(c.targetEbitdaMin) : null;
  const regions = c?.primaryGeographies ?? [];
  const geo: Fact = !c
    ? { label: "Geography", value: "Not set" }
    : c.geoFocus === "focused"
      ? { label: "Geography", value: regions.length ? regions.slice(0, 2).join(", ") : "Focused", sub: regions.length > 2 ? `+${regions.length - 2} more` : null }
      : { label: "Geography", value: "National" };
  return [
    processesFact(s) ?? { label: "In processes", value: "None" },
    lastTouchFact(b.lastActivityAt, today),
    { label: "Platform size", value: size ?? "Not set", sub: ebitda ? `Min EBITDA ${ebitda}` : null },
    geo,
    ftcContactFact(s),
  ];
}

/** When the prospect last moved into this stage (moves are newest first). */
export function stageSince(moves: StageMove[] | undefined, stage: string | null): string | null {
  return moves?.find((m) => m.stage === stage)?.at ?? null;
}

/** One line of a prospect's stage history. */
export function stageMoveLine(m: StageMove, today: string): { when: string; text: string; detail: string | null } {
  return {
    when: dayLabel(m.at, today),
    text: m.stage ? `Moved to ${m.stage}` : "Stage cleared",
    detail: m.automatic ? (m.why ? `Automatic: ${m.why}` : "Automatic") : m.by ? `by ${firstName(m.by)}` : null,
  };
}

export interface ProspectFactsInput {
  stage: string | null; status: string | null; holdRevisitDate: string | null; lastActivityAt: string | null;
  targetAmount: string | number | null; estimatedDealValue: string | number | null; aiDealValue: string | null;
}

export function prospectFacts(
  s: RecordSummary | undefined, today: string, p: ProspectFactsInput, origination: OriginationView | null | undefined,
): Fact[] {
  const revisit = p.holdRevisitDate ? p.holdRevisitDate.slice(0, 10) : null;
  const since = stageSince(s?.stageMoves, p.stage);
  const stage: Fact = p.status === "On Hold"
    ? { label: "Stage", value: "Nurturing", sub: revisit ? `revisit ${dayLabel(revisit, today)}` : "no revisit date", ...(revisit && revisit < today ? { tone: "warn" as const } : {}) }
    : { label: "Stage", value: p.stage ?? "Not set", sub: since ? `since ${dayLabel(since, today)}` : null };

  // Same rule as /api/prospects: the nearest open task on the company itself, not on one of its people.
  const next = s?.openItems.find((i) => i.onCompany);
  const nextStep: Fact = next
    ? {
        label: "Next step",
        value: next.title,
        sub: [firstName(next.ownerName), next.dueOn ? `${next.overdue ? "was due " : ""}${dayLabel(next.dueOn, today)}` : null].filter(Boolean).join(" · ") || null,
        ...(next.overdue ? { tone: "bad" as const } : {}),
      }
    : { label: "Next step", value: "None set" };

  const d = origination ? describeOrigination(origination) : null;
  const source: Fact = d ? { label: "Source", value: d.primary, sub: d.kind } : { label: "Source", value: "Not recorded" };

  // The financials basis order: the team's target, then the fee estimate, then the AI's guess.
  const manual = money(p.targetAmount) ?? money(p.estimatedDealValue);
  const value: Fact = manual
    ? { label: "Est. deal value", value: manual }
    : p.aiDealValue
      ? { label: "Est. deal value", value: p.aiDealValue, sub: "AI estimate" }
      : { label: "Est. deal value", value: "Not set" };

  return [stage, nextStep, source, value, lastTouchFact(p.lastActivityAt, today)];
}

export interface LendingFacts {
  capitalTypes: string[] | null; minDealSize: string | null; maxDealSize: string | null;
  sweetSpot: string | null; speedToClose: string | null;
}

export function capitalProviderFacts(
  s: RecordSummary | undefined, today: string, cp: { lastActivityAt: string | null; profile: LendingFacts | null },
): Fact[] {
  const p = cp.profile;
  const types = p?.capitalTypes ?? [];
  const size = p ? dealSizeLabel(p.minDealSize, p.maxDealSize) : null;
  return [
    { label: "Capital types", value: types.length ? types.slice(0, 2).join(", ") : "Not set", sub: types.length > 2 ? `+${types.length - 2} more` : null },
    { label: "Deal size", value: size ?? "Not set", sub: p?.sweetSpot ? `sweet spot ${p.sweetSpot}` : null },
    { label: "Speed to close", value: p?.speedToClose || "Not set" },
    lastTouchFact(cp.lastActivityAt, today),
    ftcContactFact(s),
  ];
}

/** A firm with no page of its own (the COI category): the conversation facts only. */
export function companyFacts(s: RecordSummary | undefined, today: string): Fact[] {
  return [lastSpokeFact(s, today), lastEmailFact(s, today), openItemsFact(s), ftcContactFact(s)];
}
